import filterInstances from "./utils/filter-instances";
import getElementsMap from "./utils/get-elements-map";
import { G_elements } from "./utils/globals/elements";
import {
  G_activeTriggerElements,
  G_appliedClasses,
  G_targetElements,
  G_timeouts,
} from "./utils/globals/globals";
import { G_runningElements } from "./utils/globals/running";
import { G_variables, G_variablesAsync } from "./utils/globals/variables";
import removeInstance from "./utils/remove-instance";
import WINDOW from "./utils/window";

/**
 * Destroy (remove all instances) of the Group or Main class
 * @param {IterableElementsProps | null} [targets]
 * @param {Object | null} options
 */
const destroy = (targets = null, options = null) => {
  /** Get the array of elements from querySelector. If no elements provided, use all `G_elements` */
  const elements = getElementsMap(targets) || G_elements;

  /** Filter the "Main" [data-tor] elements */
  const mains = /** @type {Array<MainClassProps>} */ (
    filterInstances(elements, "isMain")
  );

  const groups = /** @type {Array<GroupClassProps>} */ (
    filterInstances(elements, "isGroup")
  );

  const containers = /** @type {Array<ContainerClassProps>} */ (
    filterInstances(elements, "isContainer")
  );

  for (const main of mains) {
    const element = main.element;

    /** Remove CSS of active properties */
    for (const item of main.activeProperties.keys()) {
      element.style.removeProperty(item);
    }

    /** Remove CSS of initial properties */
    for (const item of main.cssInitials) {
      element.style.removeProperty(item[1]);
    }

    /** Remove local listener on the element */
    for (const { event, target, callback } of main.listeners) {
      target.removeEventListener(/** @type {"click"} */ (event), callback);
      main.element.removeEventListener(/** @type {"click"} */ (event), callback);
    }

    main.listeners.clear();

    /** Remove the "transform" if "shift" is applied */
    if (main.shift[0]) {
      element.style.removeProperty("transform");
    }

    /** Remove element bounds on the element */
    delete element["tor-bounds"];

    /** Remove element from global "running" map */
    G_runningElements.delete(element);

    G_variablesAsync.delete(element);

    /** Remove element instance */
    removeInstance(element, "Main");
  }

  for (const group of groups) {
    /** Remove element instance */
    removeInstance(group.element, "Group");
  }

  for (const container of containers) {
    /** Remove element instance */
    removeInstance(container.element, "Container");
  }

  /** Get global observers */
  const observers = /** @type {WindowObserverProps} */ (
    WINDOW.get("observers")
  );

  /** Get global listeners */
  const listeners = /** @type {WindowListenersProps} */ (
    WINDOW.get("listeners")
  );

  /** Remove all if no targets */
  if (!targets) {
    G_activeTriggerElements.clear();
    G_variables.clear();

    /** Remove global listener */
    for (const [event, fn] of listeners) {
      if (fn) {
        window.removeEventListener(event, fn[0]);
        listeners.set(/** @type {"scroll"} */ (event), null);
      }
    }

    /** Remove observers only if no targets - complete destroy */
    for (const [name, observer] of observers) {
      if (observer) {
        observer.disconnect();
        // observers.set(name, null);
        observers.delete(name);
      }
    }

    for (const target of G_targetElements) {
      const bounds = target["tor-bounds"];

      if (bounds) {
        delete target["tor-bounds"];
      }

      removeInstance(target, "Target");
    }
  }

  /** Remove classes applied by TorusKit */
  for (const [node, set] of G_appliedClasses) {
    const element = /** @type {HTMLElement} */ (node);

    for (const name of set) {
      element.classList.remove(name);
    }
  }

  /** Remove timeouts */
  for (const timeout of G_timeouts) {
    clearTimeout(timeout);
  }
};

export default destroy;
